import { GateMovementState2 as MovementState } from '@/__generated__/graphql';
import { OverlayState } from '@components/GateFigure';
import { RED } from '@features/gateOperationModal/constants';
import { type I18n } from '@lingui/core';
import { t, Trans } from '@lingui/macro';
import { CheckCircleOutline, HighlightOff, Login } from '@mui/icons-material';
import { teal } from '@mui/material/colors';
import { FC, PropsWithChildren } from 'react';
import { InMovementIcon } from '../components/figure/InMovementIcon';
import { InQueueIcon } from '../components/figure/InQueueIcon';
import { Accent, ErrorAccent } from './accents';

const FINALIZED_STATES = [MovementState.Finalized, MovementState.FinalizedWithError];
const INITIALIZING_STATES = [MovementState.InQueue, MovementState.Initializing];

export const translateMovementState = (state: MovementState, i18n: I18n) => {
  const translationMap: Record<MovementState, string> = {
    [MovementState.InQueue]: t(i18n)`En cola`,
    [MovementState.Initializing]: t(i18n)`Inicializando`,
    [MovementState.InMovement]: t(i18n)`En movimiento`,
    [MovementState.Finalized]: t(i18n)`Finalizada`,
    [MovementState.FinalizedWithError]: t(i18n)`Finalizada con error`,
  };
  return translationMap[state];
};

export const isFinalizedState = (state: MovementState) => {
  return FINALIZED_STATES.includes(state);
};

export const isInitializingState = (state: MovementState) => {
  return INITIALIZING_STATES.includes(state);
};

type MovementStateColor = {
  color: string;
  contrastColor: string;
};

export const getStateColor = (state: MovementState) => {
  const whiteColors: MovementStateColor = {
    color: '#ffffff',
    contrastColor: '#000000',
  };

  const greenColors: MovementStateColor = {
    color: '#26A69A',
    contrastColor: '#ffffff',
  };

  const redColors: MovementStateColor = {
    color: '#923E4A',
    contrastColor: '#ffffff',
  };

  const colorMap: Record<MovementState, MovementStateColor> = {
    [MovementState.InQueue]: whiteColors,
    [MovementState.Initializing]: whiteColors,
    [MovementState.InMovement]: whiteColors,
    [MovementState.Finalized]: greenColors,
    [MovementState.FinalizedWithError]: redColors,
  };

  return colorMap[state];
};

export const overlayStateMap = (state: MovementState) => {
  const translationMap: Record<MovementState, OverlayState> = {
    [MovementState.InQueue]: {
      id: 1,
      icon: InQueueIcon,
      label: (
        <Trans>
          Iniciando <Accent>conexión</Accent>
          <br />
          con la compuerta
        </Trans>
      ),
      iconColor: 'white',
      iconContrastColor: 'black',
    },
    [MovementState.Initializing]: {
      id: 2,
      icon: Login,
      label: (
        <Trans>
          <Accent>Inicio de operación</Accent> de la compuerta
        </Trans>
      ),
      iconColor: 'white',
      iconContrastColor: 'black',
    },
    [MovementState.InMovement]: {
      id: 3,
      icon: InMovementIcon,
      label: (
        <Trans>
          Compuerta en
          <br />
          <Accent>movimiento</Accent>
        </Trans>
      ),
      iconColor: 'white',
      iconContrastColor: 'black',
    },
    [MovementState.Finalized]: {
      id: 4,
      icon: CheckCircleOutline,
      label: (
        <Trans>
          Operación
          <br />
          <Accent>finalizada</Accent>
        </Trans>
      ),
      iconColor: teal[400],
      iconContrastColor: 'white',
    },
    [MovementState.FinalizedWithError]: {
      id: 5,
      icon: HighlightOff,
      label: (
        <Trans>
          Operación <ErrorAccent>finalizada con errores</ErrorAccent>
        </Trans>
      ),
      iconColor: RED,
      iconContrastColor: 'white',
    },
  };
  return translationMap[state];
};
